import { Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerTypeFormComponent } from '../data-layer-type-form/data-layer-type-form.component';
import { PoiDataLayerTypeFormComponent } from '../poi-data-layer-type-form/poi-data-layer-type-form.component';
import { NgIf } from '@angular/common';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { MenuItem } from 'primeng/api';
import { DataLayer } from '../../../models/data-layer';
import { getState } from '@ngrx/signals';
import { DataLayerType } from '../../../models/data-layer-type';
import { TabMenuModule } from 'primeng/tabmenu';
import { DataLayerFormComponent } from '../data-layer-form/data-layer-form.component';
import { FeatureFormComponent } from '../../molecules/feature-form/feature-form.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DividerModule } from 'primeng/divider';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { ButtonModule } from 'primeng/button';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { Subscription } from 'rxjs';
import { RoutingService } from '../../../services/routing.service';

@Component({
  selector: 'app-data-layer-type-overlay',
  standalone: true,
  imports: [
    DataLayerTypeFormComponent,
    PoiDataLayerTypeFormComponent,
    NgIf,
    TabMenuModule,
    DataLayerFormComponent,
    FeatureFormComponent,
    DividerModule,
    AlertComponent,
    ButtonModule,
    HeaderComponent,
    MaskComponent,
    SidebarModule,
  ],
  templateUrl: './data-layer-type-overlay.component.html',
  styleUrl: './data-layer-type-overlay.component.scss',
})
export class DataLayerTypeOverlayComponent implements OnInit, OnDestroy {
  dataLayerTypeStore = inject(DataLayerTypeStore);
  dataLayerStore = inject(DataLayerStore);
  addDataLayerForm = false;
  dataLayerMenuItems: MenuItem[] = [];
  selectedType?: DataLayerType;
  selectedLayer: DataLayer | null = null;
  typeLayers: DataLayer[] = [];
  private routerSubscriptions: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private routingService: RoutingService
  ) {
    effect(() => {
      const dataLayerTypeState = getState(this.dataLayerTypeStore);
      const layerState = getState(this.dataLayerStore);

      queueMicrotask(() => {
        if (
          this.selectedType &&
          dataLayerTypeState.selectedId !== this.selectedType._id
        ) {
          this.dataLayerTypeStore.singleSelection(this.selectedType._id);
        }

        if (
          layerState.trigger &&
          layerState.trigger.includes('isSelectable') &&
          layerState.lastPersistedId
        ) {
          setTimeout(() => {
            this.router.navigate(
              [{ outlets: { jahr: layerState.lastPersistedId } }],
              { relativeTo: this.route }
            );
          });
        } else {
          this.selectedType =
            this.dataLayerTypeStore.selectedItem() ||
            this.selectedType ||
            undefined;
          this.selectedLayer = this.dataLayerStore.selectedItem();
          this.typeLayers = this.dataLayerStore.filterByParent(
            dataLayerTypeState.selectedId
          );
          if (!this.selectedLayer && this.typeLayers && this.typeLayers[0]) {
            this.selectedLayer = this.typeLayers[0];
            this.router.navigate(
              [{ outlets: { jahr: this.selectedLayer._id } }],
              { relativeTo: this.route }
            );
          }

          this.setLayerMenu();
        }
      });
    });
  }

  /**
   * Initializes the component upon initialization.
   * Handles the extraction of route parameters to set data layer types and data layers,
   * configures single selections for data layers and data layer types,
   * and subscribes to router events to update data layers when the route changes.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    const dataLayerTypeId = this.route.snapshot.paramMap.get('id');
    const initialDataLayerId = dataLayerTypeId
      ? (this.route.snapshot.firstChild?.paramMap.get('id') ??
        this.dataLayerStore.getLatestByParent(dataLayerTypeId)?.[0]?._id)
      : undefined;

    this.dataLayerTypeStore.singleSelection(dataLayerTypeId ?? '');
    if (initialDataLayerId) {
      this.dataLayerStore.singleSelection(initialDataLayerId);
    }

    this.routerSubscriptions.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setDataLayerByRouteParam();
        }
      })
    );
  }

  /**
   * Updates the data layer selection based on the 'id' route parameter.
   * Retrieves the 'id' parameter from the route snapshot and invokes the
   * data layer store's single selection method with the retrieved id.
   * If no id is found, the selection action is not performed.
   *
   * @return {void} Does not return any value.
   */
  setDataLayerByRouteParam(): void {
    const dataLayerId =
      this.route.snapshot.firstChild?.paramMap.get('id') ?? '';

    if (dataLayerId) {
      setTimeout(() => {
        this.dataLayerStore.singleSelection(dataLayerId);
      }, 10);
    }
  }

  /**
   * Sets up the dataLayerMenuItems array with menu items for managing layers in the application.
   * Initializes the array and populates it with a default "Add Year" option and dynamic options based on the available typeLayers.
   * Each menu item contains a label, icon, and either a command or routerLink for layer selection or addition.
   *
   * @return {void} No return value.
   */
  setLayerMenu(): void {
    this.dataLayerMenuItems = [];

    this.dataLayerMenuItems.push({
      label: 'Jahr hinzufügen',
      icon: 'fas fa-circle-plus',
      command: () => {
        this.addYear();
      },
    });

    if (this.typeLayers && this.typeLayers.length) {
      this.typeLayers.forEach(layer => {
        this.dataLayerMenuItems.push({
          label: String(layer.year),
          icon: layer.published ? 'fas fa-earth-europe' : 'fas fa-house-user',
          store: this.dataLayerStore,
          command: () => {
            this.dataLayerStore.singleSelection(layer._id);
          },
          routerLink: [{ outlets: { jahr: layer._id } }],
        });
      });
    }
  }

  /**
   * Adds a year by deselecting the current data layer and toggling the data layer form to active.
   *
   * @return {void} Does not return a value.
   */
  addYear(): void {
    this.dataLayerStore.deselect();
    queueMicrotask(() => {
      this.addDataLayerForm = true;
    });
  }

  /**
   * Closes the data layer form and optionally sets the data layer based on route parameters
   * if no event is provided.
   *
   * @param {DataLayer} [event] - An optional parameter representing the data layer event. If not provided, the method sets the data layer using the route parameter.
   * @return {void} Does not return a value.
   */
  closeDataLayerForm(event?: DataLayer | undefined): void {
    this.addDataLayerForm = false;
    if (!event) {
      this.setDataLayerByRouteParam();
    }
  }

  /**
   * Lifecycle hook that is called when the directive or component is destroyed.
   * Performs cleanup operations such as unsubscribing from router subscriptions
   * and navigating to the root route using the routing service.
   *
   * @return {void} No value is returned from this method.
   */
  ngOnDestroy(): void {
    this.routerSubscriptions.unsubscribe();
    this.routingService.navigateToRoot().then(() => {
      this.dataLayerStore.deselect();
      this.dataLayerTypeStore.deselect();
    });
  }
}
